import { Injectable } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { CookieService } from 'ngx-cookie';
import { GetLeadResponseModel } from '../models/lead.model';

@Injectable()
export class TrackingService {
  private _pushedTags!: Array<string>;
  private _flowId!: string;

  constructor(
    private gtmService: GoogleTagManagerService,
    private cookieService: CookieService
  ) {
    this._pushedTags = new Array<string>();
    this._flowId = '';
  }

  setFlow(flowId: string): void {
    this._flowId = flowId;
  }

  trackFlow(pageUrl: string): void {
    const gtmTag = {
      event: 'flow',
      flowId: this._flowId,
      pagePath: pageUrl,
    };
    this.gtmService.pushTag(gtmTag);
  }

  trackPageView(pageUrl: string): void {
    const gtmTag = {
      event: 'pageview',
      pagePath: pageUrl,
    };
    this.gtmService.pushTag(gtmTag);
  }

  filledField(fieldName: string): void {
    // push GTM data layer with a custom event
    if (!Array.from(this._pushedTags.values()).includes(fieldName)) {
      const gtmTag = {
        event: 'filledField',
        data: fieldName,
      };
      this.gtmService.pushTag(gtmTag);
      this._pushedTags.push(fieldName);
    }
  }

  createdApplication(amount: number): void {
    const gtmTag = {
      event: 'createdApplication',
      data: { value: amount },
    };
    this.gtmService.pushTag(gtmTag);
  }

  submitApplication(): void {
    const gtmTag = {
      event: 'submitApplication',
      data: '',
    };
    this.gtmService.pushTag(gtmTag);
  }

  getCookie(key: string): string {
    return this.cookieService.get(key);
  }

  populateCookiesWithGetLeadResponseData(
    data: GetLeadResponseModel,
    currentRoute: string
  ) {
    this.cookieService.put('c_referer', data.utm_source);
    this.cookieService.put('c_utm_source', data.utm_source);
    this.cookieService.put('c_utm_medium', data.utm_medium);
    this.cookieService.put('c_utm_campaign', data.utm_campaign);
    this.cookieService.put('c_utm_content', data.utm_content);

    // If customers comes through /tmidentify, set utm_content to below
    if (currentRoute === 'tmidentify') {
      this.cookieService.put('c_utm_content', 'customer_completed');
    }
  }

  resetToCorrectCookies(data: any) {
    this.cookieService.put('c_referer', data.utm_source);
    this.cookieService.put('c_utm_source', data.utm_source);
    this.cookieService.put('c_utm_medium', data.utm_medium);
    this.cookieService.put('c_utm_campaign', data.utm_campaign);
  }

  populateTrackingData(data: any): any {
    data.referer = this.getTrackingReferer();
    data.trackingcode = this.getTrackingCode();
    data.clientId = this.getClientId();
    data.formtype = 'default';

    if (this.getUtmMediumCode()) {
      data.utm_medium = this.getUtmMediumCode();
    }
    if (this.getUtmSourceCode()) {
      data.utm_source = this.getUtmSourceCode();
    }
    if (this.getUtmCampaignCode()) {
      data.utm_campaign = this.getUtmCampaignCode();
    }
    if (this.getUtmContentCode()) {
      data.utm_content = this.getUtmContentCode();
    }
    if (this.getExtIDCode()) {
      data.ext_id = this.getExtIDCode();
    }
    if (data.referer === 'adtraction') {
      data.affiliate = this.getAffiliateCode();
    }
    if (
      data.utm_campaign &&
      data.utm_source &&
      data.utm_source === 'creddo_connect'
    ) {
      if (this.getCCDirect !== null) {
        data.affiliate = this.getAffiliateCode();
      } else {
        data.affiliate = this.getCCAffiliate();
      }
    }
    if (data.referer === 'addrevenue.io') {
      data.trackingcode = this.getAddrevenueClickId();
      data.channelId = this.getAddrevenueChannelId();
    }
    return data;
  }

  populateTackingDirectVisit(data: any) {
    this.cookieService.put('c_referer', document.referrer);
    this.cookieService.put('c_utm_source', data.utm_source);
    this.cookieService.put('c_utm_medium', data.utm_medium);
    this.cookieService.put('c_utm_campaign', data.utm_campaign);
    this.cookieService.put('c_utm_content', data.utm_content);
    this.cookieService.put(
      'c_affiliate',
      this.sliceReferrer(document.referrer)
    );
    this.cookieService.put('cc_direct', 'direct');
  }

  // Remove http or https from url-string and only return url before '/' (like location.host)
  sliceReferrer(url: string): string {
    let slicedUrl = url;
    if (url.startsWith('https://')) {
      const https = 'https://';
      slicedUrl = url.slice(https.length);
    }

    if (url.startsWith('http://')) {
      const http = 'http://';
      slicedUrl = url.slice(http.length);
    }

    const splitUrl = slicedUrl.split('/');
    return splitUrl[0];
  }

  generateClientId(): string {
    // Generate 9 digit number
    const id = Math.floor(100000000 + Math.random() * 900000000);
    // Take epoch seconds now
    const dt = Math.round(Date.now() / 1000);

    return id + '.' + dt;
  }

  getClientId(): string {
    const cliendId = this.getCookie('_ga');
    if (!cliendId) {
      return this.generateClientId();
    }
    return cliendId.substr(6);
  }

  getTrackingCode(): string | null {
    return this.getCookie('c_trackingcode') || null;
  }

  getAffiliateCode(): string | null {
    return this.getCookie('c_affiliate') || null;
  }

  getUtmMediumCode(): string | null {
    return this.getCookie('c_utm_medium') || null;
  }

  getUtmSourceCode(): string | null {
    return this.getCookie('c_utm_source') || null;
  }

  getUtmCampaignCode(): string | null {
    return this.getCookie('c_utm_campaign') || null;
  }

  getUtmContentCode(): string | null {
    return this.getCookie('c_utm_content') || null;
  }

  getExtIDCode(): string | null {
    return this.getCookie('c_extid') || null;
  }

  getTrackingReferer(): string | null {
    return this.getCookie('c_referer') || null;
  }

  getAddrevenueChannelId(): string | null {
    return localStorage.getItem('addrevenueChannelId') || null;
  }

  getAddrevenueClickId(): string | null {
    return localStorage.getItem('addrevenueClickId') || null;
  }

  getCCAffiliate(): string | null {
    return this.getCookie('cc_affiliate') || null;
  }

  getCCDirect(): string | null {
    return this.getCookie('cc_direct') || null;
  }
}
